import { openDB, type DBSchema } from "idb";

import { ApiError, postJson, request, requestBlob } from "./api";
import type { Annotation, AnnotationInput, Book } from "./types";

interface CachedBook {
  key: string;
  userId: number;
  bookId: string;
  blob: Blob;
  metadata?: Book;
  updatedAt: string;
}

interface OutboxItem {
  id: string;
  userId: number;
  bookId: string;
  kind: "annotation-create" | "annotation-delete" | "progress-create";
  payload: Record<string, unknown>;
  createdAt: string;
  attempts: number;
}

type CachedAnnotation = Annotation & {
  userId: number;
  bookKey: string;
};

interface FlussikDb extends DBSchema {
  bookFiles: {
    key: string;
    value: CachedBook;
    indexes: { "by-user": number };
  };
  annotations: {
    key: string;
    value: CachedAnnotation;
    indexes: { "by-user-book": string };
  };
  outbox: {
    key: string;
    value: OutboxItem;
    indexes: { "by-user": number };
  };
}

const dbPromise = openDB<FlussikDb>("flussik-reader", 1, {
  upgrade(db) {
    const books = db.createObjectStore("bookFiles", { keyPath: "key" });
    books.createIndex("by-user", "userId");
    const annotations = db.createObjectStore("annotations", { keyPath: "id" });
    annotations.createIndex("by-user-book", "bookKey");
    const outbox = db.createObjectStore("outbox", { keyPath: "id" });
    outbox.createIndex("by-user", "userId");
  }
});
const flushInFlight = new Map<number, Promise<number>>();

const bookKey = (userId: number, bookId: string) => `${userId}:${bookId}`;

export async function loadBookFile(userId: number, bookId: string, metadata?: Book): Promise<Blob> {
  const db = await dbPromise;
  const cached = await db.get("bookFiles", bookKey(userId, bookId));
  if (cached) {
    if (metadata && !cached.metadata) await db.put("bookFiles", { ...cached, metadata });
    return cached.blob;
  }
  const blob = await requestBlob(`/api/books/${bookId}/file`);
  await db.put("bookFiles", {
    key: bookKey(userId, bookId),
    userId,
    bookId,
    blob,
    metadata,
    updatedAt: new Date().toISOString()
  });
  return blob;
}

export async function cachedBook(userId: number, bookId: string): Promise<CachedBook | undefined> {
  const db = await dbPromise;
  return db.get("bookFiles", bookKey(userId, bookId));
}

export async function isBookOffline(userId: number, bookId: string): Promise<boolean> {
  const db = await dbPromise;
  return Boolean(await db.get("bookFiles", bookKey(userId, bookId)));
}

export async function queueAnnotation(
  userId: number,
  bookId: string,
  input: AnnotationInput
): Promise<Annotation> {
  const db = await dbPromise;
  const now = new Date().toISOString();
  const id = input.id ?? crypto.randomUUID();
  const annotation: Annotation & { userId: number; bookKey: string } = {
    id,
    userId,
    bookKey: bookKey(userId, bookId),
    book_id: bookId,
    annotation_type: input.annotation_type,
    cfi_range: input.cfi_range ?? null,
    spine_index: input.spine_index ?? null,
    spine_href: input.spine_href ?? null,
    char_start: input.char_start ?? null,
    char_end: input.char_end ?? null,
    selected_text: input.selected_text,
    prefix: input.prefix ?? "",
    suffix: input.suffix ?? "",
    note: input.note ?? "",
    version: 1,
    deleted: false,
    created_at: now,
    updated_at: now,
    pending: true
  };
  await db.put("annotations", annotation);
  await db.put("outbox", {
    id: `annotation:${id}`,
    userId,
    bookId,
    kind: "annotation-create",
    payload: { ...input, id },
    createdAt: now,
    attempts: 0
  });
  window.dispatchEvent(new Event("flussik:outbox"));
  return annotation;
}

export async function queueAnnotationDeletion(
  userId: number,
  bookId: string,
  annotation: Annotation
): Promise<Annotation> {
  const db = await dbPromise;
  const now = new Date().toISOString();
  const createOutboxId = `annotation:${annotation.id}`;
  const pendingCreate = await db.get("outbox", createOutboxId);
  const deletedAnnotation: CachedAnnotation = {
    ...annotation,
    userId,
    bookKey: bookKey(userId, bookId),
    deleted: true,
    updated_at: now,
    pending: !pendingCreate
  };

  await db.put("annotations", deletedAnnotation);
  if (pendingCreate?.kind === "annotation-create") {
    await db.delete("outbox", createOutboxId);
  } else {
    await db.put("outbox", {
      id: `annotation-delete:${annotation.id}`,
      userId,
      bookId,
      kind: "annotation-delete",
      payload: { annotationId: annotation.id, baseVersion: annotation.version },
      createdAt: now,
      attempts: 0
    });
  }
  window.dispatchEvent(new Event("flussik:outbox"));
  return deletedAnnotation;
}

export async function queueProgress(
  userId: number,
  bookId: string,
  payload: Record<string, unknown>
): Promise<void> {
  const db = await dbPromise;
  const id = String(payload.id ?? crypto.randomUUID());
  await db.put("outbox", {
    id: `progress:${id}`,
    userId,
    bookId,
    kind: "progress-create",
    payload: { ...payload, id },
    createdAt: new Date().toISOString(),
    attempts: 0
  });
  window.dispatchEvent(new Event("flussik:outbox"));
}

export async function localAnnotations(userId: number, bookId: string): Promise<Annotation[]> {
  const db = await dbPromise;
  return db.getAllFromIndex("annotations", "by-user-book", bookKey(userId, bookId));
}

export async function pullAnnotations(userId: number, bookId: string): Promise<Annotation[]> {
  const remote = await request<Annotation[]>(
    `/api/reading/books/${bookId}/annotations?include_deleted=true`
  );
  const db = await dbPromise;
  const pendingLocal = new Set(
    (await db.getAllFromIndex("annotations", "by-user-book", bookKey(userId, bookId)))
      .filter((annotation) => annotation.pending)
      .map((annotation) => annotation.id)
  );
  const tx = db.transaction("annotations", "readwrite");
  for (const annotation of remote) {
    if (pendingLocal.has(annotation.id)) continue;
    await tx.store.put({
      ...annotation,
      userId,
      bookKey: bookKey(userId, bookId),
      pending: false
    });
  }
  await tx.done;
  return localAnnotations(userId, bookId);
}

export function flushOutbox(userId: number): Promise<number> {
  const active = flushInFlight.get(userId);
  if (active) return active;
  const flush = flushOutboxOnce(userId).finally(() => {
    if (flushInFlight.get(userId) === flush) flushInFlight.delete(userId);
  });
  flushInFlight.set(userId, flush);
  return flush;
}

async function flushOutboxOnce(userId: number): Promise<number> {
  if (!navigator.onLine) return 0;
  const db = await dbPromise;
  const items = await db.getAllFromIndex("outbox", "by-user", userId);
  let flushed = 0;
  for (const item of items.sort((a, b) => a.createdAt.localeCompare(b.createdAt))) {
    try {
      if (item.kind === "annotation-create") {
        const saved = await postJson<Annotation>(
          `/api/reading/books/${item.bookId}/annotations`,
          item.payload
        );
        await db.put("annotations", {
          ...saved,
          userId,
          bookKey: bookKey(userId, item.bookId),
          pending: false
        });
      } else if (item.kind === "annotation-delete") {
        const annotationId = String(item.payload.annotationId);
        const baseVersion = Number(item.payload.baseVersion);
        const saved = await request<Annotation>(
          `/api/reading/annotations/${encodeURIComponent(annotationId)}?base_version=${baseVersion}`,
          { method: "DELETE" }
        );
        await db.put("annotations", {
          ...saved,
          userId,
          bookKey: bookKey(userId, item.bookId),
          pending: false
        });
      } else {
        await postJson(`/api/reading/books/${item.bookId}/progress`, item.payload);
      }
      await db.delete("outbox", item.id);
      flushed += 1;
    } catch (reason) {
      if (item.kind === "annotation-delete" && reason instanceof ApiError && reason.status === 404) {
        const annotationId = String(item.payload.annotationId);
        const cached = await db.get("annotations", annotationId);
        if (cached) await db.put("annotations", { ...cached, deleted: true, pending: false });
        await db.delete("outbox", item.id);
        flushed += 1;
        continue;
      }
      await db.put("outbox", { ...item, attempts: item.attempts + 1 });
      if (!(reason instanceof ApiError) || reason.status >= 500) break;
    }
  }
  return flushed;
}

export async function outboxCount(userId: number): Promise<number> {
  const db = await dbPromise;
  return db.countFromIndex("outbox", "by-user", userId);
}
